// MCO Calculator tab — Rogers walkthrough layout (readonly operation, stress sliders,
// formula boxes). No plan selector, no educational blurbs, no bottom-line footer.

import { useEffect, useMemo, useState } from "react";

import { Alert, Box, Card, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { buildMcoCalculatorWalk } from "api-toolkit/mco/mcoCalculatorWalk";
import { formatNumber, formatPrice } from "utility/formatters";

import StressSlider from "./StressSlider";

const fm = (v) =>
  "$" +
  Math.round(Number(v) || 0).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
const f2 = (v) => formatPrice(Number(v) || 0, 2);
const pct = (v) => `${Math.round(Number(v) * 100)}%`;

function Sect({ children }) {
  return (
    <Typography
      variant="caption"
      component="div"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mt: 2.5,
        mb: 1,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: "text.secondary",
        "&::after": { content: '""', flex: 1, height: "1px", bgcolor: "divider" },
      }}
    >
      {children}
    </Typography>
  );
}

function FormulaBox({ title, rma, rows }) {
  return (
    <Card variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
        {rma ? (
          <Typography variant="caption" color="text.secondary">
            RMA: {rma}
          </Typography>
        ) : null}
      </Stack>
      {rows.map(([formula, result], i) => (
        <Stack
          key={i}
          direction="row"
          justifyContent="space-between"
          gap={1}
          flexWrap="wrap"
          sx={{
            mt: 0.5,
            fontFamily: "ui-monospace, Menlo, monospace",
            fontSize: 12,
            color: "text.secondary",
          }}
        >
          <Box component="span">{formula}</Box>
          <Typography
            component="span"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              fontSize: 14,
            }}
          >
            {result}
          </Typography>
        </Stack>
      ))}
    </Card>
  );
}

export default function McoCalculatorTab({ mco, geo }) {
  const base = mco.stressBaseline;
  const cropHp = mco.priceLegs?.[0]?.harvestPrice;
  const pp = base?.projectedPrice;

  const [harvestPrice, setHarvestPrice] = useState(null);
  const [yieldPct, setYieldPct] = useState(100);
  const [costPct, setCostPct] = useState(100);

  useEffect(() => {
    if (!base || !(Number(pp) > 0)) return;
    const settledHp = cropHp != null && Number(cropHp) > 0 ? Number(cropHp) : null;
    setHarvestPrice(settledHp ?? Number(pp));
    setYieldPct(100);
    setCostPct(100);
    // Seed from Official baseline only — not on every mco object identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: seed key is the Official numbers
  }, [base?.projectedPrice, base?.expectedYield, base?.acres, cropHp]);

  const hp = Number(harvestPrice) > 0 ? Number(harvestPrice) : Number(pp) > 0 ? Number(pp) : 0;

  const walk = useMemo(
    () =>
      buildMcoCalculatorWalk(
        base,
        { harvestPrice: hp, yieldPct: yieldPct / 100, costPct: costPct / 100 },
        mco.premium,
        { practiceName: geo?.practice_name }
      ),
    [base, hp, yieldPct, costPct, mco.premium, geo?.practice_name]
  );

  if (!base) {
    return <Alert severity="info">Calculator unavailable — need Official actuarial data.</Alert>;
  }
  if (!walk.ready) {
    return <Alert severity="info">{walk.blockers?.join("; ") || "Calculator unavailable."}</Alert>;
  }

  const { unit, county, protection, premium, harvest, payment, totals, stress } = walk;
  const hpMin = Math.round(county.pp * 0.75 * 100) / 100;
  const hpMax = Math.max(hpMin + 0.01, Math.round(county.pp * 1.2 * 100) / 100);

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Sect>1 · your operation</Sect>
      <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 1 }}>
        <Table size="small" sx={{ "& td, & th": { fontVariantNumeric: "tabular-nums" } }}>
          <TableHead>
            <TableRow>
              <TableCell>Unit</TableCell>
              <TableCell align="right">APH bu</TableCell>
              <TableCell align="right">Acres</TableCell>
              <TableCell align="right">Share %</TableCell>
              <TableCell align="right">Coverage %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{unit.practiceName}</TableCell>
              <TableCell align="right">{formatNumber(unit.aph, 0)}</TableCell>
              <TableCell align="right">{formatNumber(unit.acres, 0)}</TableCell>
              <TableCell align="right">{formatNumber(unit.share * 100, 0)}</TableCell>
              <TableCell align="right">{formatNumber(unit.coveragePercent * 100, 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>

      <Sect>
        {county.estimates
          ? "2 · what you expect the county to lock on sep 14"
          : "2 · what the county locked on sep 14"}
      </Sect>
      <FormulaBox
        title="Expected revenue per acre"
        rma="expected revenue"
        rows={[[`county yield ${county.eay} bu × September price ${f2(county.pp)}`, f2(county.er)]]}
      />
      <FormulaBox
        title="Expected margin per acre"
        rma="expected margin"
        rows={[[`revenue ${f2(county.er)} − expected costs ${f2(county.ecost)}`, f2(county.em)]]}
      />
      <FormulaBox
        title="Where MCO starts paying"
        rma="trigger margin"
        rows={[
          [`you absorb the first ${county.dedPct}% of expected revenue: ${f2(county.er)} × ${county.dedPct}%`, f2(county.ded)],
          [`margin ${f2(county.em)} − ${f2(county.ded)}`, f2(county.tm)],
        ]}
      />
      <FormulaBox
        title="How much cover you have from there"
        rma="coverage value"
        rows={[
          [`${f2(county.er)} × ${Math.round(county.cr * 100)}% of expected revenue`, f2(county.cv)],
          [
            `maxed out once margin falls to ${f2(county.tm)} − ${f2(county.cv)}`,
            f2(Math.round((county.tm - county.cv) * 100) / 100),
          ],
        ]}
      />

      <Sect>3 · your protection</Sect>
      <FormulaBox
        title="As-sold protection — September lock"
        rma="protection"
        rows={[
          [
            `${unit.aph} bu APH × ${f2(county.pp)} × ${unit.acres} ac × ${pct(unit.share)} share × ${Math.round(county.cr * 100)}% × ${pct(unit.coveragePercent)} coverage`,
            fm(protection.prot0),
          ],
        ]}
      />

      <Sect>4 · what it costs you</Sect>
      {premium.hasRate ? (
        <FormulaBox
          title="Premium, and who pays it"
          rma="premium & subsidy"
          rows={[
            [`county rate ${(premium.rate * 100).toFixed(2)}% × ${fm(protection.prot0)}`, fm(premium.total)],
            [`the government pays ${pct(premium.subsidy)}`, `− ${fm(premium.gov)}`],
            ["you pay", fm(premium.you)],
          ]}
        />
      ) : (
        <FormulaBox
          title="Premium, and who pays it"
          rma="premium & subsidy"
          rows={[
            ["county rate", "not published yet"],
            [`the government pays ${pct(premium.subsidy)} of it`, ""],
            ["you pay", "—"],
          ]}
        />
      )}

      <Sect>5 · this year&apos;s assumptions</Sect>
      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 1 }}>
        <StressSlider
          label="Harvest price"
          valueLabel={`${f2(hp)}/bu`}
          min={hpMin}
          max={hpMax}
          step={0.01}
          value={hp}
          onChange={setHarvestPrice}
        />
        <StressSlider
          label="County yields"
          valueLabel={`${yieldPct}% of expected`}
          min={70}
          max={115}
          step={1}
          value={yieldPct}
          onChange={setYieldPct}
        />
        <StressSlider
          label="Input costs"
          valueLabel={`${costPct}% of expected`}
          min={70}
          max={145}
          step={1}
          value={costPct}
          onChange={setCostPct}
        />
      </Stack>

      <Sect>6 · how the season resolves</Sect>
      <FormulaBox
        title="Harvest revenue per acre"
        rma="final revenue"
        rows={[
          [
            `harvest price ${f2(harvest.usePrice)} × county yield ${stress.finalYield.toFixed(1)} bu`,
            f2(harvest.revenue),
          ],
        ]}
      />
      <FormulaBox
        title="Harvest margin per acre"
        rma="final margin"
        rows={[
          [
            `revenue ${f2(harvest.revenue)} − input costs at ${costPct}% of expected ${f2(stress.harvestCosts)}`,
            f2(harvest.margin),
          ],
        ]}
      />
      {harvest.recomputed && harvest.recompute ? (
        <FormulaBox
          title="October beat September — the guarantee recomputes"
          rma="harvest price recompute"
          rows={[
            [
              `revenue re-expected at ${f2(hp)}: ${county.eay} bu × ${f2(hp)}`,
              f2(harvest.recompute.er2),
            ],
            ["trigger moves up to", f2(harvest.recompute.tm2)],
            ["harvest-reset protection", fm(harvest.recompute.protU)],
          ]}
        />
      ) : null}

      <Sect>7 · the payment</Sect>
      <FormulaBox
        title="Share of the maximum, then your check"
        rma="payment factor × protection = indemnity"
        rows={[
          [
            `trigger ${f2(payment.trigger)} − margin ${f2(payment.margin)}`,
            payment.short > 0 ? `${f2(payment.short)} short` : "no shortfall",
          ],
          [
            `shortfall ÷ the ${f2(payment.cover)} of cover (capped at 100%)`,
            `${Math.round(payment.pf * 100)}%`,
          ],
          [
            `${Math.round(payment.pf * 100)}% × ${harvest.recomputed ? "harvest-reset" : "as-sold"} protection ${fm(payment.protU)}`,
            fm(payment.check),
          ],
        ]}
      />

      <Sect>8 · all units together</Sect>
      <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table size="small" sx={{ "& td, & th": { fontVariantNumeric: "tabular-nums" } }}>
          <TableHead>
            <TableRow>
              <TableCell>Unit</TableCell>
              <TableCell align="right">As-sold protection</TableCell>
              <TableCell align="right">Your premium</TableCell>
              <TableCell align="right">Share of max</TableCell>
              <TableCell align="right">Check</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{unit.practiceName}</TableCell>
              <TableCell align="right">{fm(totals.protection)}</TableCell>
              <TableCell align="right">{totals.youPremium != null ? fm(totals.youPremium) : "—"}</TableCell>
              <TableCell align="right">{`${Math.round(totals.pf * 100)}%`}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {fm(totals.check)}
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {fm(totals.protection)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {totals.youPremium != null ? fm(totals.youPremium) : "—"}
              </TableCell>
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {fm(totals.check)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>
      {harvest.recomputed ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          As-sold is the September lock ({fm(protection.prot0)}). The payment uses harvest-reset
          protection ({fm(payment.protU)}) because October beat September.
        </Typography>
      ) : null}
    </Box>
  );
}
